import axios from 'fe-axios';

// API endpoints should ideally be relative, however we need an absolute endpoint
// when pre-rendering the Public App Directory app in order to reach the service from Jenkins.
// REACT_APP_API is defined in public-app-directory and connects to the skyline bridge internally.
const API_PATH =
  process.env.NODE_ENV === 'production' && navigator.userAgent === 'ReactSnap'
    ? `${process.env.REACT_APP_API}/service/app-directory/v1`
    : '/service/app-directory/v1';

const ADMIN_SERVICE_PATH =
  process.env.NODE_ENV === 'development'
    ? '/public/v1/admin'
    : '/service/app-directory/v1/admin';
const ADMIN_BASE_PATH =
  process.env.NODE_ENV === 'development' ? `http://localhost:8080` : ``;
const ADMIN_API_PATH = ADMIN_BASE_PATH + ADMIN_SERVICE_PATH;

// Possible arguments for apps, categories endpoint
const CATEGORY_IDS = 'categoryIds';
const FEATURE_STATUS = 'featureStatus';
const PAGE_NUMBER = 'pageNumber';
const PAGE_SIZE = 'pageSize';
const SORT = 'sort';
const SORT_BY = 'sortBy';
const SORT_DIR = 'sortDirection';
const SLUG = 'slug';
const SEARCH_TERM = 'searchTerm';
const SORT_FIELD = 'sortField';
const SORT_ORDER = 'sortOrder'

const GET_APPS_ARGS = [
  CATEGORY_IDS,
  FEATURE_STATUS,
  PAGE_NUMBER,
  PAGE_SIZE,
  SORT,
  SORT_BY,
  SORT_DIR,
  SORT_FIELD,
  SORT_ORDER,
  SLUG,
  SEARCH_TERM,
];

/**
 * Serializes an object to an URI encoded query string.
 * Values must be Strings or Arrays.
 *
 * @param {Object} obj - query options
 * @returns {String} - query string
 */
const serialize = (obj) =>
  Object.entries(obj)
    .map((entry) =>
      [
        entry[0],
        [].concat(entry[1]).map((arrayEntry) => encodeURIComponent(arrayEntry)),
      ].join('='),
    )
    .join('&');

/**
 * Builds endpoint from query arguments object
 *
 * @param {Object} queryArguments - query arguments object
 * @returns {String} - query string
 */
const buildFilteredEndpoint = (endpoint, queryArguments = {}) => {
  const queryArgumentKeys = Object.keys(queryArguments);
  const validQueryArgumentKeys = queryArgumentKeys.filter((arg) =>
    GET_APPS_ARGS.includes(arg),
  );

  return validQueryArgumentKeys.length
    ? `${API_PATH}/${endpoint}?${serialize(queryArguments)}`
    : `${API_PATH}/${endpoint}`;
};

/**
 * Builds `getApps` endpoint from query arguments object
 *
 * @param {Object} queryArguments - query arguments object
 * @returns {String} - query string
 */
const buildGetAdminAppsEndpoint = (queryArguments = {}) => {
  const queryArgumentKeys = Object.keys(queryArguments);
  const validQueryArgumentKeys = queryArgumentKeys.filter((arg) =>
    ['appProviderId'].includes(arg),
  );

  return validQueryArgumentKeys.length
    ? `${ADMIN_API_PATH}/apps?${serialize(queryArguments)}`
    : `${ADMIN_API_PATH}/apps`;
};

/**
 * Gets all provider data from endpoint
 *
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 * @returns {Array} - An array of all provider data from the endpoint
 */
const getProviders = () => {
  return axios.get(`${ADMIN_API_PATH}/providers`).then(({ data }) => {
    return data.data;
  });
};

/**
 * Gets provider data for the provider with the given ID from endpoint
 *
 * @param {number|string} id - The ID of the provider
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 * @returns {Object} - The data from the endpoint for the given provider
 */
const getProviderById = (id) => {
  return axios.get(`${ADMIN_API_PATH}/providers/${id}`).then(({ data }) => {
    return data.data;
  });
};

/**
 * Gets all app data from endpoint
 *
 * @param {Object} queryArguments
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 * @returns {Array} - An array of all app data from the endpoint
 */
const getApps = (queryArguments = {}) => {
  return axios
    .get(buildFilteredEndpoint('apps', queryArguments))
    .then(({ data }) => {
      return data.data;
    });
};

/**
 * Gets all app data from endpoint
 *
 * @param {Object} queryArguments
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 * @returns {Array} - An array of all app data from the endpoint
 */
const getAdminApps = (queryArguments = {}) => {
  return axios
    .get(buildGetAdminAppsEndpoint(queryArguments))
    .then(({ data }) => {
      return data.data;
    });
};

/**
 * Gets app data for the app with the given ID from endpoint
 *
 * @param {number|string} id - The ID of the app
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 * @returns {Object} - The data from the endpoint for the given app
 */
const getAppById = (id) => {
  return axios.get(`${API_PATH}/apps/${id}`).then(({ data }) => {
    return data.data;
  });
};

/**
 * Gets admin app data for the app with the given ID from endpoint
 * @param {number|string} id - The ID of the app
 * @returns {Object} - The data from the endpoint for the given app
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 */
const getAdminAppById = (id) => {
  return axios.get(`${ADMIN_API_PATH}/apps/${id}`).then(({ data }) => {
    return data.data;
  });
};

/**
 * Get related apps by Id
 *
 * @param {Number|String} appId - of the app
 * @returns {Object[]} - array of app objects related to the app
 */
const getRelatedAppsByAppId = (appId) => {
  return axios.get(`${API_PATH}/apps/${appId}/related`).then(({ data }) => {
    return data.data;
  });
};

/**
 * Gets all integrationPoints data from endpoint
 *
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 * @returns {Array} - An array of all integrationPoint data from the endpoint
 */
const getIntegrationPoints = () => {
  return axios.get(`${API_PATH}/integrationpoints`).then(({ data }) => {
    return data.data;
  });
};

/**
 * Gets integrationPoint data for the integrationPoint with the given ID from endpoint
 *
 * @param {number|string} id - The ID of the integrationPoint
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 * @returns {Object} - The data from the endpoint for the given integrationPoint
 */
const getIntegrationPointById = (id) => {
  return axios.get(`${API_PATH}/integrationpoints/${id}`).then(({ data }) => {
    return data.data;
  });
};

/**
 * Gets all the categories from endpoint
 *
 * @throws {Error} - When endpoint does not respond, or receives non-2XX response
 * @returns {Array} - An array of all the categories from the endpoint
 */
const getCategories = (queryArguments = {}) => {
  return axios
    .get(buildFilteredEndpoint('categories', queryArguments))
    .then(({ data }) => {
      return data.data;
    });
};

/**
 * Gets all category types from App Directory Service
 *
 * @returns {{ id: Number, description: String, name: String }[]} - array of all category types
 */
const getCategoryTypes = () => {
  return axios.get(`${API_PATH}/category-types`).then(({ data }) => {
    return data.data;
  });
};

export {
  getProviders,
  getProviderById,
  getApps,
  getAdminApps,
  getAppById,
  getAdminAppById,
  getRelatedAppsByAppId,
  getIntegrationPoints,
  getIntegrationPointById,
  getCategories,
  getCategoryTypes,
  serialize,
  buildFilteredEndpoint,
};
